import { Link, useNavigate } from "react-router-dom";
import { Search, ShoppingBag, User, MapPin, LogOut, Package, ChevronDown } from "lucide-react";
import { useAuthStore, useCartStore, useUIStore } from "@/lib/stores";
import { useState } from "react";
import Logo from "@/components/Logo";

export default function Header() {
    const { user, logout } = useAuthStore();
    const { count } = useCartStore();
    const { setLoginOpen, setCartOpen } = useUIStore();
    const nav = useNavigate();
    const [q, setQ] = useState("");
    const [menuOpen, setMenuOpen] = useState(false);

    const onSearch = (e) => {
        e.preventDefault();
        if (q.trim()) nav(`/catalog?q=${encodeURIComponent(q.trim())}`);
    };

    return (
        <header
            data-testid="main-header"
            className="sticky top-0 z-40 bg-rk-bg/95 backdrop-blur-xl"
        >
            {/* Top strip */}
            <div className="hidden md:flex items-center justify-between text-[10px] uppercase tracking-[0.28em] text-rk-muted px-6 lg:px-12 py-2 bg-rk-navy text-rk-gold-soft/80">
                <div className="flex items-center gap-2">
                    <MapPin className="w-3 h-3" /> DELIVERING ACROSS INDIA
                </div>
                <div className="flex items-center gap-5">
                    <Link to="/gifts/corporate" className="rk-link-gold hover:text-rk-gold">Corporate Gifting</Link>
                    <span className="w-px h-3 bg-rk-gold/30" />
                    <Link to="/admin/login" className="rk-link-gold hover:text-rk-gold" data-testid="admin-link">Admin</Link>
                </div>
            </div>

            <div className="border-b border-rk-border">
                <div className="px-4 md:px-8 lg:px-12 max-w-[1400px] mx-auto py-4 md:py-5 flex items-center gap-3 md:gap-6">
                    <Link to="/" data-testid="logo-link" className="shrink-0">
                        <Logo />
                    </Link>

                    <form onSubmit={onSearch} className="flex-1 max-w-xl ml-2">
                        <div className="relative">
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-rk-navy/60" />
                            <input
                                data-testid="search-input"
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                placeholder="Search 250+ brands, categories, occasions…"
                                className="w-full h-11 border border-rk-navy/20 bg-white pl-11 pr-4 text-sm placeholder:text-rk-navy/40 focus:outline-none focus:border-rk-gold focus:ring-1 focus:ring-rk-gold"
                            />
                        </div>
                    </form>

                    <nav className="hidden lg:flex items-center gap-6 text-[11px] uppercase tracking-[0.2em] font-semibold text-rk-navy">
                        <Link to="/catalog" className="rk-link-gold" data-testid="nav-all-brands">All Brands</Link>
                        <Link to="/gifts/birthday" className="rk-link-gold">Occasions</Link>
                    </nav>

                    <div className="flex items-center gap-2 md:gap-3">
                        {user ? (
                            <div className="relative">
                                <button
                                    data-testid="user-menu-btn"
                                    onClick={() => setMenuOpen((o) => !o)}
                                    className="flex items-center gap-2 border border-rk-navy/20 px-3 py-2 text-xs font-semibold uppercase tracking-wider text-rk-navy hover:border-rk-gold transition-colors"
                                >
                                    <User className="w-4 h-4" />
                                    <span className="hidden sm:inline max-w-[90px] truncate">
                                        {user.name || `+91 ${user.phone?.slice(0, 5)}`}
                                    </span>
                                    <ChevronDown className="w-3 h-3" />
                                </button>
                                {menuOpen && (
                                    <div
                                        onMouseLeave={() => setMenuOpen(false)}
                                        className="absolute right-0 mt-2 w-56 bg-white border border-rk-border shadow-2xl py-2 text-sm"
                                    >
                                        <Link to="/orders" onClick={() => setMenuOpen(false)} data-testid="my-orders-link"
                                            className="flex items-center gap-2 px-4 py-2.5 hover:bg-rk-cream text-rk-navy">
                                            <Package className="w-4 h-4" /> My Orders
                                        </Link>
                                        <Link to="/vouchers" onClick={() => setMenuOpen(false)} data-testid="my-vouchers-link"
                                            className="flex items-center gap-2 px-4 py-2.5 hover:bg-rk-cream text-rk-navy">
                                            <ShoppingBag className="w-4 h-4" /> My Vouchers
                                        </Link>
                                        <div className="rk-divider-gold my-1" />
                                        <button
                                            onClick={() => { logout(); setMenuOpen(false); nav("/"); }}
                                            data-testid="logout-btn"
                                            className="flex items-center gap-2 w-full px-4 py-2.5 text-left hover:bg-rk-cream text-rk-navy"
                                        >
                                            <LogOut className="w-4 h-4" /> Log out
                                        </button>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <button
                                data-testid="login-btn"
                                onClick={() => setLoginOpen(true)}
                                className="rk-btn-ghost hidden sm:inline-block !py-2.5 !px-5"
                            >
                                Log in
                            </button>
                        )}

                        <button
                            data-testid="cart-btn"
                            onClick={() => setCartOpen(true)}
                            className="relative rk-btn-primary !py-2.5 !px-5 inline-flex items-center gap-2"
                        >
                            <ShoppingBag className="w-4 h-4" />
                            <span className="hidden sm:inline">Cart</span>
                            {count() > 0 && (
                                <span className="absolute -top-2 -right-2 bg-rk-navy text-rk-gold text-[10px] w-5 h-5 rounded-full flex items-center justify-center font-bold border border-rk-gold">
                                    {count()}
                                </span>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </header>
    );
}
